import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import cn from "classnames";
import BaseComponent from "meta/features/base-component";
import { Container } from "meta/ui2/layout";
import LockScreen from "./components/LockScreenContainer";
import InactivityLock from "./components/InactivityLock";
import InactivityOverlay from "./components/InactivityOverlay";
import MainContent from "./components/MainContentContainer";
import AppHeader from "./features/AppHeader/AppHeader";
import "./style/style.less";
import "./style/icons/icons.less";
import "./style/fonts/roboto.less";
import { withHooks } from "./hooks/withHooks";
import { useChangeTabEventBroadcasting } from "./webevents/useChangeTabEventBroadcasting";
import { withSnackbar } from "notistack";
import { DialogManager } from "meta/ui2/dialog/DialogManager";
import {
  setContentTitle,
  clearPatientFromTab,
  progressStart,
  progressDone,
  updateUserInfo,
  setContentLocked,
  appReset,
  setContentModified,
} from "./features/slices/rootSlice";
import { setNotifications } from "./features/slices/notificationsSlice";
import { useAppSelector } from "./hooks/useAppSelector";
import { useCatchWebEvents } from "./webevents/useCatchWebEvents";
import StartupLoading from "./features/StartupLoading/StartupLoading";
import { useEventsWebEvents } from "./webevents/useEventsWebEvents";
import { useHeartBeat } from "./features/heartBeat";
import JsonParams from "meta/features/json-params";
import { useRootAPI } from "./hooks/RootAPI/useRootAPI";
import { useDashboardInteraction } from "./webevents/useDashboardInteraction";
import { ResizerBackDrop } from "./features/RightPanelFeatures/Resizer/ResizerBackDrop";
import { useInacivityAutoLogout } from "./hooks/useInactivityAutoLogout";
import { useCheckFdbLicense } from "./hooks/useCheckFdbLicense";
import { useCheckCosign } from "./hooks/useCheckCosign";
import { shouldShowFdbLicenseCheck } from './hooks/useCheckFdbLicense'
import { usePrintingAllowed } from "./hooks/usePrintingAllowed";
import { useClientTimeCookies } from "./hooks/useClientTimeCookies";
import { useSearch } from "./hooks/useSearch";

class Root extends BaseComponent {
  constructor(props) {
    super(props);

    this.iframesRefsDictionary = {};
    this.currentIframe = null;

    this._bind("handleMessage");
  }

  _handlePageHide() {
    localStorage.removeItem('appRunning');
    const { CsrfToken } = JsonParams;
    // sendBeacon is used here as it survives page teardown (as opose to regular AJAX GET call)
    navigator.sendBeacon(`eMAR/MCLogout.aspx?CsrfDebug=e&CsrfToken=${CsrfToken}`);
  }

  findTabByFrameSession = (frameSession) => {
    const { tabs } = this.props.root;
    return (
      tabs &&
      Object.keys(tabs).find((key) => tabs[key].frameSession === frameSession)
    );
  };

  handleMessage(m) {
    const { progressStart, progressDone } = this.props;

    switch (m.data.type) {
      case "BEGIN_REQUEST":
      case "END_REQUEST":
        const tab = this.findTabByFrameSession(m.data._frameSession);
        const action =
          m.data.type === "BEGIN_REQUEST" ? progressStart : progressDone;
        if (tab) {
          action(tab);
        }
        break;
      default:
        break;
    }
  }

  componentDidMount() {
    localStorage.setItem('appRunning', 'true');
    window.addEventListener("message", this.handleMessage);
    Object.defineProperties(window, { DialogManager: { get: DialogManager } });
    $(window).off("beforeunload");
    window.addEventListener("pagehide", this._handlePageHide);
  }

  render() {
    const { isLogged, tabs, activeTabKey, inactivityLogoutActive, headlessLoginResult } = this.props.root;
    const activeTab = tabs[activeTabKey];
    const { frameSession } = activeTab;

    let currentTabKey;
    if (activeTab.children) {
      currentTabKey = activeTab.children[activeTab.activeTabKey];
    } else {
      currentTabKey = activeTabKey;
    }

    const showFdbLicenseCheck = shouldShowFdbLicenseCheck(this.props.root);
    if (showFdbLicenseCheck) {
      // the rendering of the FDB license check related UI is done by hook useCheckFdbLicense as a side effect
      // ideally we'd want to render the UI directly here instead of imperatively callling the Dialog to show it

      // do not render the application till the FDB license checks are cleared
      return (
        <Container/>
      )
    }

    if (headlessLoginResult && !headlessLoginResult.success) {
      // if this will show other errors as well, then it should get better styles
      // the error should also show the application name, but I don't think that's desirable for the headless integration
      return (
        <Container fit column className="appErrorMessage">
          {headlessLoginResult.failureReason}
          <a href="#" onClick={() => window.location.reload()}>Retry</a>
        </Container>
      )
    }

    this.currentIframe = this.iframesRefsDictionary[currentTabKey];
    return (
      <Container
        fit
        column
        className={cn({ root: true, "panel-layout": isLogged })}
      >
        <StartupLoading />
        <ResizerBackDrop />
        <Container flex="0 0 auto">
          <AppHeader frameSession={frameSession} />
        </Container>
        <Container flex="1 0 auto">
          <MainContent
            onIframeRef={(key, iframe) =>
              (this.iframesRefsDictionary[key] = iframe)
            }
          />
        </Container>
        {!headlessLoginResult ? (<InactivityLock />) : null}
        {!headlessLoginResult ? (<InactivityOverlay userLoggedIn={isLogged} inactivityLogoutActive={inactivityLogoutActive} />) : null}
        <LockScreen />
      </Container>
    );
  }
}

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      setContentTitle,
      clearPatientFromTab,
      updateUserInfo,
      setContentModified,
      appReset,
      setContentLocked,
      progressStart,
      progressDone,
      set_notification: setNotifications,
    },
    dispatch
  );

export default withHooks(
  connect(null, mapDispatchToProps)(withSnackbar(Root)),
  useChangeTabEventBroadcasting,
  () => useAppSelector((state) => state),
  useCatchWebEvents,
  useEventsWebEvents,
  useDashboardInteraction,
  useHeartBeat,
  useRootAPI,
  useInacivityAutoLogout,
  useCheckFdbLicense,
  useCheckCosign,
  usePrintingAllowed,
  useClientTimeCookies,
  useSearch
);
